import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Badge,
  Popover,
  ListItemText as MuiListItemText,
  Chip,
  CircularProgress,
  Tooltip,
  Paper,
  Fade,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import FactoryIcon from '@mui/icons-material/Factory';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import BuildIcon from '@mui/icons-material/Build';
import AirIcon from '@mui/icons-material/Air';
import GrainIcon from '@mui/icons-material/Grain';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScienceIcon from '@mui/icons-material/Science';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RedeemIcon from '@mui/icons-material/Redeem';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BusinessIcon from '@mui/icons-material/Business';
import PostAddIcon from '@mui/icons-material/PostAdd';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HistoryIcon from '@mui/icons-material/History';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import GetAppIcon from '@mui/icons-material/GetApp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TableViewIcon from '@mui/icons-material/TableView';
import SpeedIcon from '@mui/icons-material/Speed';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ContactsIcon from '@mui/icons-material/Contacts';
import { tasksAppService, purchaseOrderService, notificationService, telegramService } from '../services/api';
import { useCheckedNavigate } from '../hooks/useCheckedNavigate';
import MessengerWidget from './MessengerWidget';
import { getNotificationDestination } from '../utils/notificationNavigation';
import {
  playNotificationSound,
  unlockNotificationSound,
} from '../utils/notificationSound';
import {
  getWebPushStatus,
  isRunningAsPwa,
  isWebPushSupported,
  tryEnableWebPush,
} from '../services/pushSubscription';
import { canAccessShiftFloor } from '../utils/shiftFloorAccess';
import { AppBarBackButton } from './PageBackHeader';
import { LEARNING_BADGES_EVENT } from '../pages/LearningPage';
import { TASKS_BADGE_EVENT } from '../pages/TasksAppPage';

const MAX_TOAST_NOTIFS = 3;
const TOAST_AUTO_HIDE_MS = 8000;

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const productionPaths = [
  '/shift-floor',
  '/production',
  '/production-operator',
  '/production-operator-v2',
  '/production-drying',
  '/production-mixing',
  '/qc-kanban',
  '/qc-stock-page',
  '/work-processes',
  '/machines',
  '/press-forms',
  '/products',
  '/maintenance-plan',
  '/maintenance',
  '/crushing',
];
const stockPaths = ['/stock'];
const settingsPaths = ['/settings-roles', '/settings-staff', '/settings-quality', '/settings-order-periods', '/settings-additional-processes'];
const purchasesPaths = ['/purchases', '/purchase-approvals', '/suppliers', '/supplier-catalog'];

function buildMenuItems(currentUser, tasksNewCount = 0, purchaseApprovalsPendingCount = 0, learningBadges = {}) {
  const perms = currentUser?.effective_permissions || {};

  const has = (key) => {
    if (!key) return true;
    return perms[key] === true;
  };

  const hasPurchaseApprovals = perms['menu.purchase_approvals'] === true;
  const canSeePurchaseApprovalQueue = hasPurchaseApprovals || has('menu.purchases');

  const items = [];

  // Порядок: Головна → Моніторинг цеху → Задачі → Замовлення → …
  if (has('menu.dashboard')) {
    items.push({ id: 'dashboard', text: 'Головна', icon: <DashboardIcon />, path: '/' });
  }

  if (canAccessShiftFloor(currentUser)) {
    items.push({
      id: 'shift-floor',
      text: 'Моніторинг цеху',
      icon: <SpeedIcon />,
      path: '/shift-floor',
    });
  }

  if (has('menu.tasks')) {
    items.push({
      id: 'tasks',
      text: 'Задачі',
      icon: <AssignmentTurnedInIcon />,
      path: '/tasks',
      badgeCount: tasksNewCount > 0 ? tasksNewCount : 0,
    });
  }

  if (hasPurchaseApprovals && !has('menu.purchases')) {
    items.push({
      id: 'purchase-approvals',
      text: 'Погодження закупівель',
      icon: <FactCheckOutlinedIcon />,
      path: '/purchase-approvals',
      badgeCount: purchaseApprovalsPendingCount > 0 ? purchaseApprovalsPendingCount : 0,
    });
  }

  if (has('menu.customer_orders')) {
    items.push({
      id: 'orders-section',
      text: 'Замовлення',
      icon: <ShoppingCartIcon />,
      path: '/customer-orders',
      matchPaths: ['/customer-orders', '/customers'],
    });
  }

  // CRM (Twenty) — лише явний true
  if (perms['menu.crm'] === true) {
    items.push({ id: 'crm', text: 'CRM', icon: <ContactsIcon />, path: '/crm' });
  }

  if (has('menu.purchases')) {
    items.push({
      id: 'purchases-section',
      text: 'Закупівлі',
      icon: <ReceiptLongIcon />,
      path: '/purchases',
      matchPaths: ['/purchases', '/purchase-approvals', '/suppliers', '/supplier-catalog'],
    });
  }

  if (has('menu.production') || has('menu.crushing')) {
    const children = [];
    if (has('menu.production.planner')) {
      children.push({ id: 'planner-v2', text: 'Планер 2.0', icon: <BuildIcon />, path: '/planner-v2' });
    }
    if (has('menu.production.warehouse')) {
      children.push({ id: 'production-warehouse', text: 'Склад виробництва', icon: <BuildIcon />, path: '/production-warehouse' });
    }
    if (has('menu.production.operator')) {
      children.push({
        id: 'operator-v2',
        text: 'Робоче місце оператора 2.0',
        icon: <BuildIcon />,
        path: '/production-operator-v2',
      });
    }
    if (has('menu.production.work_processes')) {
      children.push({
        id: 'work-processes',
        text: 'Робочі процеси',
        icon: <BuildIcon />,
        path: '/work-processes',
      });
    }
    if (has('menu.production.setup')) {
      children.push({
        id: 'setup',
        text: 'Наладка',
        icon: <PrecisionManufacturingIcon />,
        path: '/setup',
      });
    }
    if (has('menu.production.drying')) {
      children.push({
        id: 'production-drying',
        text: 'Сушіння',
        icon: <AirIcon />,
        path: '/production-drying',
      });
    }
    if (has('menu.production.mixing')) {
      children.push({
        id: 'production-mixing',
        text: 'Лінія змішування',
        icon: <GrainIcon />,
        path: '/production-mixing',
      });
    }
    if (has('menu.production.qc_kanban')) {
      children.push({
        id: 'qc-kanban',
        text: 'Контроль якості (Канбан)',
        icon: <FactCheckOutlinedIcon />,
        path: '/qc-kanban',
      });
    }
    if (has('menu.production.qc_stock')) {
      children.push({
        id: 'qc-stock-page',
        text: 'Склад контролю якості',
        icon: <FactCheckOutlinedIcon />,
        path: '/qc-stock-page',
      });
    }
    if (has('menu.crushing')) {
      children.push({
        id: 'crushing',
        text: 'Дроблення',
        icon: <ScienceIcon />,
        path: '/crushing',
      });
    }
    if (has('menu.production.maintenance')) {
      children.push({
        id: 'maintenance-plan',
        text: 'План обслуговування',
        icon: <ScheduleIcon />,
        path: '/maintenance-plan',
      });
      children.push({
        id: 'maintenance',
        text: 'Обслуговування',
        icon: <BuildIcon />,
        path: '/maintenance',
      });
    }
    if (has('menu.production.products')) {
      children.push({ id: 'products', text: 'Вироби', icon: <InventoryIcon />, path: '/products' });
      children.push({ id: 'product-kits', text: 'Комплекти виробів', icon: <InventoryIcon />, path: '/product-kits' });
    }
    if (has('menu.production.machines')) {
      children.push({ id: 'machines', text: 'ТПА', icon: <FactoryIcon />, path: '/machines' });
    }
    if (has('menu.production.pressforms')) {
      children.push({
        id: 'press-forms',
        text: 'Прес-форми',
        icon: <PrecisionManufacturingIcon />,
        path: '/press-forms',
      });
    }
    if (children.length) {
      items.push({ id: 'production', text: 'Виробництво', icon: <BuildIcon />, children });
    }
  }

  if (has('menu.packaging')) {
    items.push({
      id: 'packaging',
      text: 'Упаковка',
      icon: <RedeemIcon />,
      path: '/packaging',
    });
  }

  if (perms['menu.packaging_daily_norm'] === true) {
    items.push({
      id: 'packaging-daily-norm',
      text: 'Норма пакування на день',
      icon: <CalendarMonthIcon />,
      path: '/packaging-daily-norm',
    });
  }

  if (has('menu.stock')) {
    items.push({ id: 'stock', text: 'Склади', icon: <WarehouseIcon />, path: '/stock' });
  }

  // LMS — лише явний true (не з’являється у всіх ролей випадково)
  if (perms['menu.lms'] === true || perms['menu.lms.manage'] === true) {
    const lmsChildren = [];
    if (perms['menu.lms'] === true || perms['menu.lms.manage'] === true) {
      lmsChildren.push({
        id: 'lms-courses',
        text: 'Курси',
        icon: <MenuBookIcon />,
        path: '/lms/courses',
      });
      lmsChildren.push({
        id: 'lms-tests',
        text: 'Тести',
        icon: <SchoolIcon />,
        path: '/lms/tests',
      });
      lmsChildren.push({
        id: 'lms-results',
        text: perms['menu.lms.manage'] === true ? 'Дашборд' : 'Результати',
        icon: <AssessmentIcon />,
        path: '/lms/results',
      });
    }
    if (perms['menu.lms.manage'] === true) {
      lmsChildren.push({
        id: 'lms-materials',
        text: 'Матеріали',
        icon: <MenuBookIcon />,
        path: '/lms/materials',
      });
      lmsChildren.push({
        id: 'lms-sync',
        text: 'AI / Sync',
        icon: <SchoolIcon />,
        path: '/lms/sync',
      });
    }
    if (lmsChildren.length) {
      items.push({ id: 'lms', text: 'Навчання', icon: <MenuBookIcon />, children: lmsChildren });
    }
  }

  // Навчання NEW (окремий сервіс learning/, iframe) — лише явний true; пункти меню сервісу — тут, як підпункти
  if (perms['menu.learning_new'] === true || perms['menu.learning_new.manage'] === true) {
    const learningChildren = [
      { id: 'learning-courses', text: 'Курси', icon: <MenuBookIcon />, path: '/learning/courses',
        badgeCount: learningBadges['/courses'] || 0 },
      { id: 'learning-tests', text: 'Пройти тест', icon: <AssignmentTurnedInIcon />, path: '/learning/tests',
        badgeCount: learningBadges['/tests'] || 0 },
      { id: 'learning-results', text: 'Результати', icon: <AssessmentIcon />, path: '/learning/results' },
    ];
    if (perms['menu.learning_new.manage'] === true) {
      learningChildren.push({ id: 'learning-make-quiz', text: 'Тести (створення)', icon: <PostAddIcon />, path: '/learning/make-quiz' });
      if (perms['menu.settings.roles'] === true) {
        learningChildren.push({ id: 'learning-settings', text: 'Налаштування ШІ', icon: <SecurityIcon />, path: '/learning/settings' });
      }
    }
    items.push({ id: 'learning-new', text: 'Навчання NEW', icon: <SchoolIcon />, children: learningChildren });
  }

  if (has('menu.settings')) {
    const children = [];
    if (has('menu.settings.roles')) {
      children.push({
        id: 'roles',
        text: 'Ролі',
        icon: <SecurityIcon />,
        path: '/settings-roles',
      });
    }
    if (has('menu.settings.staff')) {
      children.push({
        id: 'staff',
        text: 'Персонал',
        icon: <PeopleIcon />,
        path: '/settings-staff',
      });
    }
    if (has('menu.settings.quality')) {
      children.push({
        id: 'quality',
        text: 'ВКЯ',
        icon: <AssessmentIcon />,
        path: '/settings-quality',
      });
    }
    if (has('menu.settings.order_periods')) {
      children.push({
        id: 'order-periods',
        text: 'Звітні періоди замовлень',
        icon: <CalendarMonthIcon />,
        path: '/settings-order-periods',
      });
    }
    if (has('menu.production.planner')) {
      children.push({
        id: 'settings-additional-processes',
        text: 'Додаткові виробничі процеси',
        icon: <BuildIcon />,
        path: '/settings-additional-processes',
      });
    }
    if (has('menu.export')) {
      children.push({
        id: 'export',
        text: 'Експорт',
        icon: <FileDownloadIcon />,
        path: '/export',
      });
    }
    if (has('menu.import')) {
      children.push({
        id: 'import',
        text: 'Імпорт',
        icon: <UploadFileIcon />,
        path: '/import',
      });
    }
    if (children.length) {
      items.push({ id: 'settings', text: 'Налаштування', icon: <BuildIcon />, children });
    }
  }

  if (has('menu.leads')) {
    items.push({ id: 'leads', text: 'Ліди', icon: <PeopleAltIcon />, path: '/leads' });
  }
  if (has('menu.projects')) {
    items.push({ id: 'projects', text: 'Проекти', icon: <AccountTreeIcon />, path: '/projects' });
  }
  if (has('menu.rnd')) {
    items.push({ id: 'rnd', text: 'R&D', icon: <ScienceIcon />, path: '/rnd' });
  }
  if (has('menu.oee')) {
    items.push({ id: 'oee', text: 'OEE', icon: <SpeedIcon />, path: '/oee' });
  }
  // Собівартість / Фінанси — лише з явним true у ролі (як LMS)
  if (perms['menu.costing'] === true) {
    items.push({ id: 'costing', text: 'Собівартість', icon: <CalculateIcon />, path: '/costing' });
  }
  if (perms['menu.finance'] === true) {
    items.push({ id: 'finance', text: 'Фінанси', icon: <AccountBalanceWalletIcon />, path: '/finance' });
  }
  if (has('menu.activity_log')) {
    items.push({ id: 'activity-log', text: 'Журнал подій', icon: <HistoryIcon />, path: '/activity-log' });
  }
  if (has('menu.notification_rules')) {
    items.push({ id: 'notification-rules', text: 'Правила сповіщень', icon: <NotificationsActiveIcon />, path: '/notification-rules' });
  }
  if (has('menu.process_log')) {
    items.push({ id: 'process-log', text: 'Журнал процесів', icon: <AssignmentIcon />, path: '/process-log' });
  }
  if (has('menu.bulk_edit')) {
    items.push({ id: 'bulk-edit', text: 'Масове редагування', icon: <TableViewIcon />, path: '/bulk-edit' });
  }

  return items;
}

function Layout({ children, currentUser, onLogout }) {
  const [tasksNewCount, setTasksNewCount] = useState(0);
  const [purchaseApprovalsPendingCount, setPurchaseApprovalsPendingCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const [flyoutItem, setFlyoutItem] = useState(null);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', next ? '1' : '0');
      return next;
    });
    setFlyoutAnchor(null);
    setFlyoutItem(null);
  };
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifOpenRef = useRef(false);
  const [toastNotifs, setToastNotifs] = useState([]);
  /** Уже показані в тості (дедуп у сесії). */
  const seenNotifIdsRef = useRef(new Set());
  /** Макс. id непрочитаних на момент prime/останнього тоста — старі «зсуви» після Прочитано не тостимо. */
  const maxSeenNotifIdRef = useRef(0);
  const notifPrimedRef = useRef(false);
  const toastTimersRef = useRef(new Map());
  const [telegramInfo, setTelegramInfo] = useState({
    bot_configured: false,
    bot_username: null,
    telegram_linked: false,
  });
  const [telegramLinkLoading, setTelegramLinkLoading] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(() => isRunningAsPwa());
  const [pushUi, setPushUi] = useState({
    supported: false,
    permission: 'unsupported',
    subscribed: false,
  });
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState('');
  const [productionOpen, setProductionOpen] = useState(true);
  const [stockOpen, setStockOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [purchasesOpen, setPurchasesOpen] = useState(true);
  const [learningOpen, setLearningOpen] = useState(true);
  // Лічильники нових курсів/тестів з «Навчання NEW» (надсилає LearningPage)
  const [learningBadges, setLearningBadges] = useState({});
  const navigate = useCheckedNavigate(currentUser);
  const location = useLocation();

  const isProductionActive = productionPaths.some(
    (p) => location.pathname === p || (p !== '/production' && location.pathname.startsWith(p))
  );
  const isStockActive = stockPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(p)
  );
  const isSettingsActive = settingsPaths.some((p) => location.pathname.startsWith(p));
  const isPurchasesActive = purchasesPaths.some((p) => location.pathname === p);
  const isLearningActive = location.pathname.startsWith('/learning');
  // Повноекранні сторінки без внутрішніх відступів (CRM, Задачі, Навчання NEW — iframe на всю робочу область)
  const isOperatorV2 =
    ['/production-operator-v2', '/planner-v2', '/crm', '/tasks'].includes(location.pathname) || isLearningActive;

  useEffect(() => {
    if (isLearningActive) setLearningOpen(true);
  }, [isLearningActive]);
  useEffect(() => {
    const onBadges = (e) => setLearningBadges(e.detail || {});
    window.addEventListener(LEARNING_BADGES_EVENT, onBadges);
    return () => window.removeEventListener(LEARNING_BADGES_EVENT, onBadges);
  }, []);

  useEffect(() => {
    if (isProductionActive) setProductionOpen(true);
  }, [isProductionActive]);
  useEffect(() => {
    if (isStockActive) setStockOpen(true);
  }, [isStockActive]);
  useEffect(() => {
    if (isSettingsActive) setSettingsOpen(true);
  }, [isSettingsActive]);
  useEffect(() => {
    if (isPurchasesActive) setPurchasesOpen(true);
  }, [isPurchasesActive]);

  const perms = currentUser?.effective_permissions || {};
  const canPollTasksBadge = Boolean(currentUser?.id) && perms['menu.tasks'] === true;
  const canPollPurchaseApprovalsBadge =
    perms['menu.purchase_approvals'] === true || perms['menu.purchases'] !== false;

  const refreshTasksBadge = useCallback(() => {
    if (!canPollTasksBadge) {
      setTasksNewCount(0);
      return;
    }
    // Непрочитані сповіщення «Задач» (Synvolve Tasks)
    tasksAppService
      .badge()
      .then((r) => setTasksNewCount(Number(r.data?.unread) || 0))
      .catch(() => setTasksNewCount(0));
  }, [canPollTasksBadge]);

  useEffect(() => {
    refreshTasksBadge();
    const id = setInterval(refreshTasksBadge, 45000);
    return () => clearInterval(id);
  }, [refreshTasksBadge]);

  // Поки задачі відкриті, iframe надсилає лічильник одразу (TasksAppPage)
  useEffect(() => {
    const onBadge = (e) => setTasksNewCount(Number(e.detail) || 0);
    window.addEventListener(TASKS_BADGE_EVENT, onBadge);
    return () => window.removeEventListener(TASKS_BADGE_EVENT, onBadge);
  }, []);

  useEffect(() => {
    if (location.pathname === '/tasks') refreshTasksBadge();
  }, [location.pathname, refreshTasksBadge]);

  const refreshPurchaseApprovalsBadge = useCallback(() => {
    if (!canPollPurchaseApprovalsBadge) {
      setPurchaseApprovalsPendingCount(0);
      return;
    }
    purchaseOrderService
      .pendingApprovalCount()
      .then((r) => setPurchaseApprovalsPendingCount(Number(r.data?.count) || 0))
      .catch(() => setPurchaseApprovalsPendingCount(0));
  }, [canPollPurchaseApprovalsBadge]);

  useEffect(() => {
    refreshPurchaseApprovalsBadge();
    const id = setInterval(refreshPurchaseApprovalsBadge, 45000);
    return () => clearInterval(id);
  }, [refreshPurchaseApprovalsBadge]);

  useEffect(() => {
    const onBump = () => refreshPurchaseApprovalsBadge();
    window.addEventListener('purchase-approvals-updated', onBump);
    return () => window.removeEventListener('purchase-approvals-updated', onBump);
  }, [refreshPurchaseApprovalsBadge]);

  useEffect(() => {
    if (location.pathname === '/purchase-approvals') refreshPurchaseApprovalsBadge();
  }, [location.pathname, refreshPurchaseApprovalsBadge]);

  // ── Сповіщення ──────────────────────────────────────────────────────────────
  const dismissToastNotif = useCallback((id) => {
    setToastNotifs((prev) => prev.filter((n) => n.id !== id));
    const t = toastTimersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      toastTimersRef.current.delete(id);
    }
  }, []);

  const pushToastNotifs = useCallback((incoming) => {
    if (!incoming?.length) return;
    const newest = incoming.slice(0, MAX_TOAST_NOTIFS);
    setToastNotifs((prev) => {
      const ids = new Set(prev.map((n) => n.id));
      const fresh = newest.filter((n) => !ids.has(n.id));
      if (!fresh.length) return prev;
      return [...fresh, ...prev].slice(0, MAX_TOAST_NOTIFS);
    });
    newest.forEach((n, i) => {
      setTimeout(() => playNotificationSound(), i * 140);
      if (toastTimersRef.current.has(n.id)) {
        clearTimeout(toastTimersRef.current.get(n.id));
      }
      toastTimersRef.current.set(
        n.id,
        setTimeout(() => dismissToastNotif(n.id), TOAST_AUTO_HIDE_MS),
      );
    });
  }, [dismissToastNotif]);

  const refreshNotifCount = useCallback(() => {
    if (!currentUser?.id) return;
    notificationService
      .getAll({ is_read: false, page_size: 10, ordering: '-created_at' })
      .then((r) => {
        const items = r.data?.results || [];
        setNotifUnread(Number(r.data?.count) || items.length);
        const maxInBatch = items.reduce((m, n) => Math.max(m, Number(n.id) || 0), 0);
        if (!notifPrimedRef.current) {
          // Перший зріз: запамʼятати поточні непрочитані, тостів немає
          items.forEach((n) => seenNotifIdsRef.current.add(n.id));
          maxSeenNotifIdRef.current = Math.max(maxSeenNotifIdRef.current, maxInBatch);
          notifPrimedRef.current = true;
          return;
        }
        // Лише реально нові (id вищий за вже бачені) — не «зсув» після mark-read
        const fresh = items.filter((n) => {
          const id = Number(n.id) || 0;
          return id > maxSeenNotifIdRef.current && !seenNotifIdsRef.current.has(n.id);
        });
        if (!fresh.length) return;
        fresh.forEach((n) => seenNotifIdsRef.current.add(n.id));
        maxSeenNotifIdRef.current = Math.max(
          maxSeenNotifIdRef.current,
          ...fresh.map((n) => Number(n.id) || 0),
        );
        pushToastNotifs(fresh);
      })
      .catch(() => {});
  }, [currentUser?.id, pushToastNotifs]);

  useEffect(() => {
    // Браузер дозволяє звук лише після взаємодії користувача
    const unlock = () => unlockNotificationSound();
    window.addEventListener('pointerdown', unlock, { once: true, capture: true });
    window.addEventListener('keydown', unlock, { once: true, capture: true });
    return () => {
      window.removeEventListener('pointerdown', unlock, { capture: true });
      window.removeEventListener('keydown', unlock, { capture: true });
    };
  }, []);

  useEffect(() => {
    refreshNotifCount();
    let intervalId;

    const pollMs = () => (document.visibilityState === 'visible' ? 8000 : 60000);

    const restartInterval = () => {
      clearInterval(intervalId);
      intervalId = setInterval(refreshNotifCount, pollMs());
    };

    restartInterval();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshNotifCount();
      }
      restartInterval();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refreshNotifCount);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refreshNotifCount);
      toastTimersRef.current.forEach((t) => clearTimeout(t));
      toastTimersRef.current.clear();
    };
  }, [refreshNotifCount]);

  useEffect(() => {
    const bump = () => refreshNotifCount();
    window.addEventListener('notifications-updated', bump);
    window.addEventListener('purchase-approvals-updated', bump);
    return () => {
      window.removeEventListener('notifications-updated', bump);
      window.removeEventListener('purchase-approvals-updated', bump);
    };
  }, [refreshNotifCount]);

  const prevNotifUserIdRef = useRef(currentUser?.id);
  useEffect(() => {
    if (prevNotifUserIdRef.current === currentUser?.id) return;
    prevNotifUserIdRef.current = currentUser?.id;
    notifPrimedRef.current = false;
    seenNotifIdsRef.current = new Set();
    maxSeenNotifIdRef.current = 0;
    setToastNotifs([]);
    toastTimersRef.current.forEach((t) => clearTimeout(t));
    toastTimersRef.current.clear();
  }, [currentUser?.id]);

  const handleNotifOpen = (e) => {
    setNotifAnchor(e.currentTarget);
    notifOpenRef.current = true;
    setNotifLoading(true);
    notificationService
      .getAll({ page_size: 30 })
      .then((r) => setNotifications(r.data?.results || []))
      .catch(() => {})
      .finally(() => {
        setNotifLoading(false);
        refreshNotifCount();
      });
  };

  const handleNotifClose = () => {
    setNotifAnchor(null);
    notifOpenRef.current = false;
  };

  const handleMarkRead = (ids) => {
    notificationService.markRead(ids).then(() => {
      setNotifications((prev) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n))
      );
      setNotifUnread((prev) => Math.max(0, prev - ids.length));
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    }).catch(() => {});
  };

  const handleToastClick = (n) => {
    const dest = getNotificationDestination(n);
    if (!n.is_read) handleMarkRead([n.id]);
    dismissToastNotif(n.id);
    if (dest) {
      navigate({ pathname: dest.pathname, search: dest.search || '' });
    } else {
      const bell = document.getElementById('app-notif-bell');
      if (bell) bell.click();
    }
  };

  const handleNotifNavigate = (n, dest) => {
    if (!dest) return;
    navigate({ pathname: dest.pathname, search: dest.search || '' });
    if (!n.is_read) handleMarkRead([n.id]);
    handleNotifClose();
  };

  const handleMarkAllRead = () => {
    notificationService.markAllRead().then(() => {
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setNotifUnread(0);
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    }).catch(() => {});
  };

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    const onInstalled = () => {
      setDeferredInstallPrompt(null);
      setPwaInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    setPwaInstalled(isRunningAsPwa());
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const refreshPushUi = useCallback(async () => {
    try {
      const st = await getWebPushStatus();
      setPushUi(st);
    } catch (_) {
      setPushUi({ supported: isWebPushSupported(), permission: 'default', subscribed: false });
    }
  }, []);

  useEffect(() => {
    if (!notifAnchor) return undefined;
    refreshPushUi();
    return undefined;
  }, [notifAnchor, refreshPushUi]);

  const handleInstallPwa = async () => {
    if (!deferredInstallPrompt) return;
    try {
      deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;
      if (choice && choice.outcome === 'accepted') {
        setPwaInstalled(true);
      }
    } catch (_) {
      /* ignore */
    } finally {
      setDeferredInstallPrompt(null);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    setPushMsg('');
    try {
      const r = await tryEnableWebPush(currentUser);
      await refreshPushUi();
      if (r.ok) {
        setPushMsg('Сповіщення увімкнено');
      } else if (r.reason === 'denied') {
        setPushMsg('Дозвіл заблоковано в налаштуваннях браузера');
      } else if (r.reason === 'no_vapid') {
        setPushMsg('На сервері не налаштовано VAPID — зверніться до адміністратора');
      } else if (r.reason === 'unsupported') {
        setPushMsg('Цей браузер не підтримує push-сповіщення');
      } else {
        setPushMsg('Не вдалося увімкнути сповіщення');
      }
    } catch (_) {
      setPushMsg('Помилка підписки на сповіщення');
    } finally {
      setPushBusy(false);
    }
  };

  const loadTelegramStatus = useCallback(async () => {
    if (!currentUser?.id) {
      setTelegramInfo({ bot_configured: false, bot_username: null, telegram_linked: false });
      return;
    }
    try {
      const r = await telegramService.status();
      const d = r.data || {};
      const linked = Boolean(d.telegram_linked);
      setTelegramInfo({
        bot_configured: Boolean(d.bot_configured),
        bot_username: d.bot_username || null,
        telegram_linked: linked,
      });
      try {
        const raw = localStorage.getItem('currentUser');
        if (raw) {
          const u = JSON.parse(raw);
          if (Number(u.id) === Number(currentUser.id)) {
            u.telegram_linked = linked;
            localStorage.setItem('currentUser', JSON.stringify(u));
          }
        }
      } catch {
        /* ignore */
      }
    } catch {
      setTelegramInfo({ bot_configured: false, bot_username: null, telegram_linked: false });
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadTelegramStatus();
  }, [loadTelegramStatus]);

  useEffect(() => {
    if (!notifAnchor) return undefined;
    loadTelegramStatus();
    const id = setInterval(loadTelegramStatus, 4000);
    return () => clearInterval(id);
  }, [notifAnchor, loadTelegramStatus]);

  const handleTelegramConnect = async () => {
    setTelegramLinkLoading(true);
    try {
      const r = await telegramService.linkRequest();
      const link = r.data?.deep_link;
      if (link) window.open(link, '_blank', 'noopener,noreferrer');
      else alert('Не вдалося отримати посилання');
    } catch (e) {
      const d = e.response?.data;
      const msg = d?.detail || (typeof d === 'string' ? d : JSON.stringify(d || e.message));
      alert(typeof msg === 'string' ? msg : 'Помилка підключення');
    } finally {
      setTelegramLinkLoading(false);
    }
  };

  const handleTelegramUnlink = async () => {
    if (!window.confirm('Відключити Telegram? Сповіщення перестануть надходити в месенджер.')) return;
    try {
      await telegramService.unlink();
      await loadTelegramStatus();
    } catch {
      alert('Не вдалося відключити');
    }
  };

  // Єдиний акцент для тостів/списку (без строкатої палітри по типах)
  const NOTIF_ACCENT = '#1565c0';
  // ────────────────────────────────────────────────────────────────────────────

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = buildMenuItems(currentUser, tasksNewCount, purchaseApprovalsPendingCount, learningBadges);

  const renderDrawer = (isCollapsed) => (
    <div>
      <Toolbar sx={{ justifyContent: isCollapsed ? 'center' : 'space-between', px: isCollapsed ? 1 : 2 }}>
        {!isCollapsed && (
          <Typography variant="h6" noWrap component="div">
            ERP Replast
          </Typography>
        )}
        <Tooltip title={isCollapsed ? 'Розгорнути меню' : 'Згорнути меню'} placement="right">
          <IconButton size="small" onClick={toggleCollapsed} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          if (item.children) {
            const isOpen =
              item.id === 'production'
                ? productionOpen
                : item.id === 'settings'
                ? settingsOpen
                : item.id === 'purchases-section'
                ? purchasesOpen
                : item.id === 'learning-new'
                ? learningOpen
                : stockOpen;
            const setOpen =
              item.id === 'production'
                ? setProductionOpen
                : item.id === 'settings'
                ? setSettingsOpen
                : item.id === 'purchases-section'
                ? setPurchasesOpen
                : item.id === 'learning-new'
                ? setLearningOpen
                : setStockOpen;

            if (isCollapsed) {
              return (
                <ListItem key={item.text} disablePadding>
                  <Tooltip title={item.text} placement="right">
                    <ListItemButton
                      sx={{ justifyContent: 'center', px: 2 }}
                      onClick={(e) => {
                        setFlyoutAnchor(e.currentTarget);
                        setFlyoutItem(item);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            }

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => setOpen(!isOpen)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {item.children.map((sub) => (
                      <ListItem key={sub.path} disablePadding>
                        <ListItemButton
                          selected={location.pathname === sub.path}
                          onClick={() => {
                            navigate(sub.path);
                            setMobileOpen(false);
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>{sub.icon}</ListItemIcon>
                          <ListItemText
                            primary={
                              sub.badgeCount ? (
                                <Badge badgeContent={sub.badgeCount} color="error">
                                  <Typography component="span" variant="inherit" sx={{ pr: 1.5 }}>
                                    {sub.text}
                                  </Typography>
                                </Badge>
                              ) : (
                                sub.text
                              )
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          const isSelected = item.matchPaths
            ? item.matchPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
            : location.pathname === item.path;

          if (isCollapsed) {
            return (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={item.text} placement="right">
                  <ListItemButton
                    selected={isSelected}
                    sx={{ justifyContent: 'center', px: 2 }}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                      {item.badgeCount ? (
                        <Badge badgeContent={item.badgeCount} color="error">
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          }

          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={
                    item.badgeCount ? (
                      <Badge badgeContent={item.badgeCount} color="error">
                        <Typography component="span" variant="body2" sx={{ pr: 1.5 }}>
                          {item.text}
                        </Typography>
                      </Badge>
                    ) : (
                      item.text
                    )
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Popover
        open={Boolean(flyoutAnchor)}
        anchorEl={flyoutAnchor}
        onClose={() => {
          setFlyoutAnchor(null);
          setFlyoutItem(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <List dense sx={{ minWidth: 200 }}>
          {(flyoutItem?.children || []).map((sub) => (
            <ListItem key={sub.path} disablePadding>
              <ListItemButton
                selected={location.pathname === sub.path}
                onClick={() => {
                  navigate(sub.path);
                  setMobileOpen(false);
                  setFlyoutAnchor(null);
                  setFlyoutItem(null);
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{sub.icon}</ListItemIcon>
                <ListItemText primary={sub.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { sm: `${collapsed ? collapsedDrawerWidth : drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <AppBarBackButton />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            ERP Replast
          </Typography>
          {currentUser && (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title="Сповіщення">
                <IconButton
                  id="app-notif-bell"
                  color="inherit"
                  onClick={handleNotifOpen}
                  size="large"
                >
                  <Badge badgeContent={notifUnread || null} color="error" max={99}>
                    {notifUnread > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                  </Badge>
                </IconButton>
              </Tooltip>
              <Typography variant="body1">
                {currentUser.full_name || currentUser.username}
              </Typography>
              <Button color="inherit" onClick={onLogout}>
                Вийти
              </Button>
            </Box>
          )}
          <Popover
            open={Boolean(notifAnchor)}
            anchorEl={notifAnchor}
            onClose={handleNotifClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: { width: 380, maxHeight: 520, display: 'flex', flexDirection: 'column' },
            }}
          >
            {/* Заголовок */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Сповіщення
                {notifUnread > 0 && (
                  <Chip
                    label={notifUnread}
                    color="error"
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: 11 }}
                  />
                )}
              </Typography>
              {notifications.some((n) => !n.is_read) && (
                <Tooltip title="Позначити всі як прочитані">
                  <IconButton size="small" onClick={handleMarkAllRead}>
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {currentUser && (
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Додаток і push-сповіщення
                </Typography>
                <Box display="flex" flexDirection="column" gap={1} sx={{ mb: 1.5 }}>
                  {pwaInstalled ? (
                    <Typography variant="body2" color="success.main">
                      Додаток встановлено
                    </Typography>
                  ) : deferredInstallPrompt ? (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<GetAppIcon />}
                      onClick={handleInstallPwa}
                    >
                      Встановити додаток
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Для встановлення: у Chrome — меню ⋮ → «Встановити додаток»; на iPhone — «Поділитися» → «На
                      екран «Домів»».
                    </Typography>
                  )}
                  {pushUi.subscribed && pushUi.permission === 'granted' ? (
                    <Typography variant="body2" color="success.main">
                      Push-сповіщення увімкнено
                    </Typography>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<NotificationsActiveIcon />}
                      onClick={handleEnablePush}
                      disabled={pushBusy || !pushUi.supported}
                    >
                      {pushBusy ? '…' : 'Увімкнути сповіщення'}
                    </Button>
                  )}
                  {pushMsg ? (
                    <Typography variant="caption" color="text.secondary">
                      {pushMsg}
                    </Typography>
                  ) : null}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Telegram — дублювання сповіщень
                </Typography>
                {!telegramInfo.bot_configured ? (
                  <Typography variant="body2" color="text.secondary">
                    На сервері не задано бота (TELEGRAM_BOT_TOKEN / TELEGRAM_BOT_USERNAME). Зверніться до
                    адміністратора.
                  </Typography>
                ) : telegramInfo.telegram_linked ? (
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Typography variant="body2">
                      Підключено до @{telegramInfo.bot_username}
                    </Typography>
                    <Button size="small" color="inherit" variant="outlined" onClick={handleTelegramUnlink}>
                      Відключити
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Натисніть кнопку, відкрийте бота в Telegram і натисніть «Start» — тоді сюди надходитимуть ті
                      самі сповіщення, що й у вікні нижче.
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleTelegramConnect}
                      disabled={telegramLinkLoading}
                    >
                      {telegramLinkLoading ? '…' : 'Підключити Telegram'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Список */}
            <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
              {notifLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <NotificationsNoneIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                  <Typography variant="body2">Немає сповіщень</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {notifications.map((n, idx) => {
                    const dest = getNotificationDestination(n);
                    return (
                    <React.Fragment key={n.id}>
                      {idx > 0 && <Divider component="li" />}
                      <ListItem
                        alignItems="flex-start"
                        secondaryAction={
                          dest ? (
                            <Button
                              size="small"
                              variant="contained"
                              sx={{ mr: 1, mt: 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNotifNavigate(n, dest);
                              }}
                            >
                              Перейти
                            </Button>
                          ) : null
                        }
                        onClick={() => !n.is_read && handleMarkRead([n.id])}
                        sx={{
                          cursor: n.is_read ? 'default' : 'pointer',
                          bgcolor: n.is_read ? 'transparent' : 'action.hover',
                          transition: 'background-color 0.2s',
                          '&:hover': { bgcolor: 'action.selected' },
                          py: 1.2,
                          px: 2,
                          gap: 1,
                          pr: dest ? 10 : 2,
                        }}
                      >
                        {/* Кольорова смужка зліва */}
                        <Box
                          sx={{
                            width: 4,
                            minWidth: 4,
                            borderRadius: 2,
                            bgcolor: n.is_read ? '#b0bec5' : NOTIF_ACCENT,
                            alignSelf: 'stretch',
                            mr: 1,
                          }}
                        />
                        <MuiListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                variant="body2"
                                fontWeight={n.is_read ? 400 : 700}
                                sx={{ flexGrow: 1, lineHeight: 1.3 }}
                              >
                                {n.title}
                              </Typography>
                              {!n.is_read && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'error.main',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {n.message && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                  sx={{ mt: 0.3, lineHeight: 1.4 }}
                                >
                                  {n.message}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="caption" color="text.disabled">
                                  {n.type_display || n.type}
                                  {' · '}
                                  {new Date(n.created_at).toLocaleString('uk-UA', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </Popover>
        </Toolbar>
      </AppBar>

      {/* Попапи нових сповіщень (до 3) під дзвіночком */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 64, sm: 72 },
          right: { xs: 8, sm: 16 },
          zIndex: (t) => t.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: { xs: 'calc(100vw - 16px)', sm: 360 },
          maxWidth: 380,
          pointerEvents: 'none',
        }}
      >
        {toastNotifs.map((n) => {
          const dest = getNotificationDestination(n);
          return (
            <Fade in key={n.id}>
              <Paper
                elevation={6}
                onClick={() => handleToastClick(n)}
                sx={{
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  borderLeft: `4px solid ${NOTIF_ACCENT}`,
                  bgcolor: '#fff',
                  opacity: 1,
                  boxShadow: '0 4px 18px rgba(0,0,0,0.14)',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeftColor: NOTIF_ACCENT,
                  '&:hover': { bgcolor: '#f7f9fc' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, pr: 0.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, color: '#0d47a1' }}>
                      {n.title}
                    </Typography>
                    {n.message ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mt: 0.5,
                          lineHeight: 1.4,
                          fontSize: 13,
                        }}
                      >
                        {n.message}
                      </Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.85 }}>
                      <Typography variant="caption" color="text.disabled">
                        {n.type_display || n.type}
                      </Typography>
                      {dest ? (
                        <Typography variant="caption" sx={{ color: NOTIF_ACCENT, fontWeight: 700 }}>
                          Перейти →
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Закрити"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissToastNotif(n.id);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            </Fade>
          );
        })}
      </Box>

      <Box
        component="nav"
        sx={{ width: { sm: collapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {renderDrawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? collapsedDrawerWidth : drawerWidth,
              overflowX: 'hidden',
              transition: (theme) => theme.transitions.create('width'),
            },
          }}
          open
        >
          {renderDrawer(collapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: isOperatorV2 ? 0 : 3,
          width: { sm: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          height: '100vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar />
        {children}
      </Box>
      {currentUser?.id ? <MessengerWidget currentUser={currentUser} /> : null}
    </Box>
  );
}

export default Layout;
